/* === AgModal -- Anagogia Design System === */

import { useEffect, useRef, useId, useState } from 'react';
import { createPortal } from 'react-dom';

import { useOverlayDismiss } from '../../../hooks/useOverlayDismiss';
import { AG_LABELS } from '../utils/agLabels';
import { useFullscreenPortalHost } from '../utils/useFullscreenPortalHost';

import { useCalqueEchap } from '../utils/useCalqueEchap';

import styles from './AgModal.module.css';

interface AgModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** Icon shown before the title */
  titleIcon?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen';
  closeOnOverlay?: boolean;
  closeOnEsc?: boolean;
  className?: string;
  /** Extra class applied to the overlay container (useful for z-index overrides) */
  overlayClassName?: string;
  /** Hide the built-in close button (useful when a custom toolbar provides its own) */
  hideCloseButton?: boolean;
  /** Supprime le padding et le défilement du conteneur de contenu :
   *  les enfants gèrent leur propre mise en page (ex. ModalNote). */
  noContentPadding?: boolean;
  /** Fermeture momentanément interdite (enregistrement ou suppression en
   *  cours) : la croix reste peinte mais grisée et inactive, et ni Échap ni un
   *  clic sur le fond ne ferment. Au sommet de la pile, la fenêtre absorbe
   *  quand même Échap : la frappe ne ferme pas celle du dessous. */
  closeDisabled?: boolean;
  /** Strate de l'overlay (`UI_STYLES.Z_INDEX.*`) quand la fenêtre s'ouvre au-dessus d'un panneau ; sinon `MODAL`. */
  zIndex?: number;
}

function AgModalPortal({
  onClose,
  title,
  titleIcon,
  children,
  footer,
  size = 'md',
  closeOnOverlay = true,
  closeOnEsc = true,
  className,
  overlayClassName,
  hideCloseButton = false,
  noContentPadding = false,
  closeDisabled = false,
  zIndex,
}: Omit<AgModalProps, 'open'>) {
  const modalRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Focus : la fenêtre le prend à l'ouverture et le rend à l'élément qui l'avait
  // (le bouton qui l'a ouverte) à la fermeture — le clavier et le lecteur d'écran
  // reviennent là où l'utilisateur en était. Le déclencheur est lu au premier
  // rendu, avant tout commit : un champ `autoFocus` du contenu ou une fenêtre
  // enfant l'auraient déjà remplacé quand l'effet de montage tourne. Un
  // déclencheur retiré du DOM entre-temps est ignoré.
  const [declencheur] = useState<HTMLElement | null>(() =>
    document.activeElement instanceof HTMLElement ? document.activeElement : null,
  );
  useEffect(() => {
    modalRef.current?.focus();
    return () => {
      if (declencheur?.isConnected) declencheur.focus({ preventScroll: true });
    };
  }, [declencheur]);

  // Pile des calques et Échap : une fenêtre ouverte par-dessus un autre calque
  // en prend le sommet ; elle le rend en se démontant, dans quelque ordre que ce
  // soit. La pile porte le seul écouteur d'Échap et n'appelle que le calque du
  // sommet (voir `utils/calqueStack.ts`) ; elle lit `onClose` et `closeOnEsc` par
  // ref, si bien que les changer ne déplace pas la fenêtre dans la pile.
  // `closeDisabled` coupe Échap sans retirer la fenêtre de la pile : au sommet,
  // elle absorbe la frappe comme une fenêtre en `closeOnEsc={false}`.
  const echapActif = closeOnEsc && !closeDisabled;
  useCalqueEchap(true, onClose, { actif: echapActif });

  // Prevent body scroll when modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // La cible du clic (et non `modalRef.contains`) évite de fermer sur les clics
  // issus de portals React (ex. EntreePanel) dont le DOM vit hors du modal mais
  // dont les événements synthétiques remontent à travers lui ; l'origine du
  // geste évite de fermer sur un relâchement hors du dialogue (sélection de texte).
  const overlayProps = useOverlayDismiss(onClose, closeOnOverlay && !closeDisabled);

  const sizeClass = styles[size] ?? styles.md;

  return (
    <div
      className={[styles.overlay, overlayClassName].filter(Boolean).join(' ')}
      style={zIndex !== undefined ? { zIndex } : undefined}
      {...overlayProps}
      /* Point d'ancrage pour les portals internes (dropdowns) : permet d'échapper
         aux `overflow` du contenu tout en restant dans le contexte d'empilement
         du modal, dont le z-index peut être surchargé par overlayClassName. */
      data-ag-modal-overlay=""
    >
      <div
        ref={modalRef}
        className={[styles.modal, sizeClass, className]
          .filter(Boolean)
          .join(' ')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
      >
        {title && (
          <div className={styles.header}>
            <h2 id={titleId} className={styles.title}>
              {titleIcon && <span className={styles.titleIcon}>{titleIcon}</span>}
              {title}
            </h2>
            {!hideCloseButton && (
              <button
                type="button"
                className={styles.closeButton}
                aria-label={AG_LABELS.fermer}
                onClick={onClose}
                disabled={closeDisabled}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M15 5L5 15M5 5l10 10"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            )}
          </div>
        )}
        {!title && !hideCloseButton && (
          <button
            type="button"
            className={[styles.closeButton, styles.closeButtonStandalone]
              .filter(Boolean)
              .join(' ')}
            aria-label={AG_LABELS.fermer}
            onClick={onClose}
            disabled={closeDisabled}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M15 5L5 15M5 5l10 10"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
        <div className={`${styles.content} ${noContentPadding ? styles.contentFlush : ''}`}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>
  );
}

/**
 * Fenêtre modale avec titre, contenu et actions.
 * @category Overlays
 */
function AgModal(props: AgModalProps) {
  // En plein écran, le navigateur ne peint que le sous-arbre de
  // `document.fullscreenElement` : accroché à `body`, le dialogue existerait
  // dans le DOM sans jamais s'afficher. L'écran qui l'ouvre n'a donc rien à
  // faire — et surtout pas à céder le plein écran.
  const portalHost = useFullscreenPortalHost(props.open);

  if (!props.open) return null;

  return createPortal(
    <AgModalPortal
      onClose={props.onClose}
      title={props.title}
      titleIcon={props.titleIcon}
      footer={props.footer}
      size={props.size}
      closeOnOverlay={props.closeOnOverlay}
      closeOnEsc={props.closeOnEsc}
      className={props.className}
      overlayClassName={props.overlayClassName}
      hideCloseButton={props.hideCloseButton}
      noContentPadding={props.noContentPadding}
      closeDisabled={props.closeDisabled}
      zIndex={props.zIndex}
    >
      {props.children}
    </AgModalPortal>,
    portalHost,
  );
}

export { AgModal };
export type { AgModalProps };
export default AgModal;
